import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@tark/shared/components';
import type { TenantTimePoll, TenantTimePollBuilderOptions, TenantTimePollSavePayload } from '@tark/shared/types';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  buildTimePollPayload,
  createDefaultTimePollDraft,
  toLocalDateTime,
  type TimePollDraft,
  validateTimePollDraft,
} from '../../lib/timePolls';

type Props = {
  options: TenantTimePollBuilderOptions;
  poll?: TenantTimePoll | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (payload: TenantTimePollSavePayload) => Promise<void>;
};

function draftFromPoll(poll: TenantTimePoll): TimePollDraft {
  return {
    programId: String(poll.programId),
    title: poll.title,
    deadlineAt: poll.deadlineAt ? toLocalDateTime(new Date(poll.deadlineAt)) : '',
    athleteIds: poll.athletes.map((athlete) => athlete.athleteId),
    slots: poll.slots.map((slot) => ({
      key: String(slot.slotId),
      startsAt: toLocalDateTime(new Date(slot.startsAt)),
      endsAt: toLocalDateTime(new Date(slot.endsAt)),
      locationLabel: slot.locationLabel || '',
      capacity: slot.capacity ? String(slot.capacity) : '',
    })),
  };
}

export default function CoachTimePollBuilder({ options, poll, saving, onCancel, onSave }: Props) {
  const [draft, setDraft] = useState<TimePollDraft>(() => poll ? draftFromPoll(poll) : createDefaultTimePollDraft());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(poll ? draftFromPoll(poll) : createDefaultTimePollDraft());
    setError(null);
  }, [poll]);

  const selectProgram = (programId: string) => {
    const numericId = Number(programId);
    const registered = options.athletes
      .filter((athlete) => athlete.registeredProgramIds.includes(numericId))
      .map((athlete) => athlete.athleteId);
    setDraft((current) => ({ ...current, programId, athleteIds: registered }));
  };

  const toggleAthlete = (athleteId: number) => {
    setDraft((current) => ({
      ...current,
      athleteIds: current.athleteIds.includes(athleteId)
        ? current.athleteIds.filter((id) => id !== athleteId)
        : [...current.athleteIds, athleteId],
    }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validateTimePollDraft(draft);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    await onSave(buildTimePollPayload(draft, options.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone));
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <Card variant="default">
        <CardHeader><CardTitle>{poll ? 'Edit time poll' : 'New time poll'}</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-text">Program</label>
              <select
                className="h-10 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={draft.programId}
                onChange={(event) => selectProgram(event.target.value)}
              >
                <option value="">Select a program</option>
                {options.programs.map((program) => <option key={program.programId} value={program.programId}>{program.name}</option>)}
              </select>
            </div>
            <Input label="Poll title" placeholder="Best time for next week's class" value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} />
            <Input label="Response deadline (optional)" type="datetime-local" value={draft.deadlineAt} onChange={(event) => setDraft((current) => ({ ...current, deadlineAt: event.target.value }))} />
          </div>

          <div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-text">Families</p>
              <span className="text-xs text-text-muted">{draft.athleteIds.length} selected</span>
            </div>
            <div className="mt-2 divide-y divide-border rounded-lg border border-border">
              {options.athletes.map((athlete) => (
                <label key={athlete.athleteId} className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 text-sm text-text">
                  <input type="checkbox" className="h-4 w-4 accent-primary" checked={draft.athleteIds.includes(athlete.athleteId)} onChange={() => toggleAthlete(athlete.athleteId)} />
                  <span>{athlete.displayName}</span>
                </label>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card variant="default">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Time options</CardTitle>
            <Button type="button" size="sm" variant="outline" leftIcon={<Plus />} disabled={draft.slots.length >= 8} onClick={() => {
              const start = new Date();
              start.setDate(start.getDate() + draft.slots.length + 1);
              start.setHours(16, 0, 0, 0);
              setDraft((current) => ({ ...current, slots: [...current.slots, { key: `new-${Date.now()}`, startsAt: toLocalDateTime(start), endsAt: toLocalDateTime(new Date(start.getTime() + 60 * 60_000)), locationLabel: '', capacity: '' }] }));
            }}>Add option</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {draft.slots.map((slot, index) => (
            <div key={slot.key} className="rounded-lg border border-border p-3">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-text">Option {index + 1}</p>
                <Button type="button" size="xs" variant="ghost" aria-label={`Remove option ${index + 1}`} disabled={draft.slots.length <= 2} onClick={() => setDraft((current) => ({ ...current, slots: current.slots.filter((item) => item.key !== slot.key) }))}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <Input label="Starts" type="datetime-local" value={slot.startsAt} onChange={(event) => setDraft((current) => ({ ...current, slots: current.slots.map((item) => item.key === slot.key ? { ...item, startsAt: event.target.value } : item) }))} />
                <Input label="Ends" type="datetime-local" value={slot.endsAt} onChange={(event) => setDraft((current) => ({ ...current, slots: current.slots.map((item) => item.key === slot.key ? { ...item, endsAt: event.target.value } : item) }))} />
                <Input label="Location (optional)" value={slot.locationLabel} onChange={(event) => setDraft((current) => ({ ...current, slots: current.slots.map((item) => item.key === slot.key ? { ...item, locationLabel: event.target.value } : item) }))} />
                <Input label="Capacity (optional)" type="number" min={1} max={999} value={slot.capacity} onChange={(event) => setDraft((current) => ({ ...current, slots: current.slots.map((item) => item.key === slot.key ? { ...item, capacity: event.target.value } : item) }))} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {error ? <p className="text-sm text-red-500">{error}</p> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" isLoading={saving}>Save draft</Button>
      </div>
    </form>
  );
}
